import { useBreakpointValue } from "@chakra-ui/react"
import useEventListener from "@pathwright/ui/src/components/hooks/useEventListener"
import React, { useContext, useEffect, useRef, useState } from "react"
import { exhaustiveGuard } from "../lib/typing"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import "./Intercom.css"
import {
  bootIntercom,
  dispatchIntercomEvent,
  isUsingPathwrightIntercom,
  loadIntercom
} from "./utils"

export type IntercomUpdateOptions = {
  horizontal_padding?: number // Horizontal padding
  vertical_padding?: number // Vertical padding
  hide_default_launcher?: boolean // Whether to hide the default Intercom launcher
  custom_launcher_selector?: string // CSS selector for custom launcher
}

// Extending the CustomEventMap with our own `PW_AUTH_TOKEN_KEY` event key.
interface CustomEventMap {
  intercom: CustomEvent<"boot" | "shutdown">
}

export type IntercomCustomEvent = CustomEventMap["intercom"]

type IntercomContextValue = {
  hideIntercomUI: () => void
  showIntercomUI: () => void
  showIntercomUIWithMessage: (message?: string) => void
  usingPathwrightIntercom: boolean
  isIntercomAvailable: boolean
}

const IntercomContext = React.createContext<IntercomContextValue>(
  {} as IntercomContextValue
)

export const useIntercomContext = () => useContext(IntercomContext)

// Hides Intercom UI after component mounts, then shows Intercom UI
// after component unmounts.
export const useToggleIntercomUI = (shouldHide?: boolean) => {
  const { hideIntercomUI, showIntercomUI } = useIntercomContext()
  useToggleIntercomVisibility(shouldHide, {
    onHide: hideIntercomUI,
    onShow: showIntercomUI
  })
}

// Hides Intercom UI after component mounts, then shows Intercom UI
// after component unmounts.
export const useToggleIntercomVisibility = (
  shouldHide: boolean | undefined,
  options: { onHide: () => void; onShow: () => void }
) => {
  const { onHide, onShow } = options

  // Simply hide Intercom until component unmounts.
  useEffect(() => {
    if (typeof shouldHide === "undefined") {
      onHide()
    }
  }, [])

  // Controlled option with shouldHide boolean.
  useEffect(() => {
    if (typeof shouldHide === "boolean") {
      shouldHide ? onHide() : onShow()
    }
  }, [shouldHide])

  // Always show Intercom when unmounting.
  useEffect(() => onShow, [])
}

export const withToggleIntercomUI =
  (Component: React.FC<any>) => (props: any) => {
    useToggleIntercomUI()
    return <Component {...props} />
  }

export const ToggleIntercomUI = withToggleIntercomUI(
  ({ children }: { children: JSX.Element }) => children || null
)

function useShouldHideIntercomUI() {
  // Hide Intercom launcher when on mobile-sized devices.
  const shouldHideIntercomUI = !!useBreakpointValue({
    base: true,
    sm: false
  })

  return shouldHideIntercomUI
}

const IntercomProvider = ({ children }: { children: JSX.Element }) => {
  // Wheter to hide based on screen size.
  const shouldHideIntercomUI = useShouldHideIntercomUI()
  // Whether Intercom is currently showing the Messenger (may
  // still be hidden by our overrides).
  const [isShowingInteromMessenger, setIsShowingInteromMessenger] =
    useState(false)

  // Using ref for closure.
  // THe explicitlyHidingIntercomUIRef indicates whether some component
  // explicitly toggled off the Intercom UI visibility.
  const explicitlyHidingIntercomUIRef = useRef(false)

  const intercomUIClassnames = [
    "hide-intercom-messenger",
    "hide-intercom-launcher"
  ]

  type IntercomUIState = "show" | "hide"

  // Handles toggling class names for hiding/showing Intercom UI.
  const toggleIntercomUI = (state: IntercomUIState) => {
    // Show Inteercom UI (conditionally, if Messenger is open).
    if (state === "show") {
      // document.body.classList.remove(...intercomUIClassnames)
      if (isShowingInteromMessenger || !explicitlyHidingIntercomUIRef.current) {
        document.body.classList.remove(...intercomUIClassnames)
      }
      // Hide Inteercom UI (conditionally, if Messenger is open).
    } else if (!isShowingInteromMessenger) {
      document.body.classList.add(...intercomUIClassnames)
    }
  }

  function explicitlyToggleIntercomUI(state: IntercomUIState) {
    explicitlyHidingIntercomUIRef.current = state === "hide"
    toggleIntercomUI(state)
  }

  const explicitlyShowIntercomUI = () => explicitlyToggleIntercomUI("show")
  const explicitlyHideIntercomUI = () => explicitlyToggleIntercomUI("hide")

  const showIntercomUIWithMessage = (message?: string) => {
    if (window.Intercom) {
      if (message) {
        window.Intercom("showNewMessage", message)
      } else {
        window.Intercom("showNewMessage")
      }
    }
  }

  useEffect(() => {
    if (isShowingInteromMessenger) {
      // Always show Intercom UI when Intercom messenger opens.
      toggleIntercomUI("show")
    } else if (
      // Conditionally hide Intercom UI when Intercom messenger closes
      // only when explicitly hiding Interom UI (when Intercom Messenger
      // isn't open).
      explicitlyHidingIntercomUIRef.current
    ) {
      toggleIntercomUI("hide")
    }
  }, [isShowingInteromMessenger])

  const [usingPathwrightIntercom, setUsePathwrightIntercomLauncher] =
    useState(false)
  const pwContext = usePathwrightContext()

  const boot = () => {
    if (window.Intercom) {
      window.Intercom("shutdown")
      dispatchIntercomEvent("shutdown")
      bootIntercom(pwContext)
      // Setup handlers for when Intercom Messenger shows/hides.
      window.Intercom("onShow", () => {
        setIsShowingInteromMessenger(true)
      })
      window.Intercom("onHide", () => {
        setIsShowingInteromMessenger(false)
      })
    }
  }

  // Hide intercom UI (can't use useToggleIntercomUI as we're
  // above context.)
  useToggleIntercomVisibility(shouldHideIntercomUI, {
    onHide: explicitlyHideIntercomUI,
    onShow: explicitlyShowIntercomUI
  })

  const [isIntercomAvailable, setIsIntercomAvailable] = useState(
    Boolean(window.Intercom)
  )

  // After intercom boots or shuts down, let's determine if we should use the Pathwright Intercom launcher.
  useEventListener(
    "intercom",
    (e: IntercomCustomEvent) => {
      switch (e.detail) {
        case "boot":
          setIsIntercomAvailable(true)
          setUsePathwrightIntercomLauncher(isUsingPathwrightIntercom(pwContext))
          break
        case "shutdown":
          setIsIntercomAvailable(false)
          setUsePathwrightIntercomLauncher(false)
          break
        default:
          exhaustiveGuard(e.detail)
      }
    },
    [pwContext]
  )

  useEffect(() => {
    loadIntercom(pwContext).then(boot)
  }, [pwContext.me])

  return (
    <IntercomContext.Provider
      value={{
        // hide/show full Intercom UI including launcher and message window
        hideIntercomUI: explicitlyHideIntercomUI,
        showIntercomUI: explicitlyShowIntercomUI,
        // show Intercom UI with optional pre-filled message
        showIntercomUIWithMessage: showIntercomUIWithMessage,
        // hide/show launcher (only Intercom launcher, not PathwrightIntercomLauncher)
        usingPathwrightIntercom: usingPathwrightIntercom,
        isIntercomAvailable: isIntercomAvailable
      }}
    >
      {children}
    </IntercomContext.Provider>
  )
}

IntercomProvider.displayName = "IntercomProvider"

export default ({ children }: { children: JSX.Element }) => (
  <IntercomProvider>{children}</IntercomProvider>
)
